import ky, { type HTTPError, type KyInstance } from "ky";
import comAutenticacaoSpace from "@/api/space/comAutenticacaoSpace";
import { env } from "@/configs/configEnv/env";
import { DEFAULT_GROUP_ID } from "@/configs/runtime";
import { DEFAULT_HEADER_GROUP_ID } from "@/configs/system";
import { anexarErroApi, lerCorpoErroApi } from "@/utils/errors/apiError";
import { getGlobalHeadersSpaceApi } from "./globalHeaders.spaceApi";
import { normalizarCabecalhoGrupoSpaceApi } from "./sessaoAutenticacao.space";

export type DominioClienteSpace =
	| "authenticator"
	| "estoqueService"
	| "pessoasService"
	| "report"
	| "compraInteligente"
	| "utilitarios"
	| "compraService"
	| "contasPagar"
	| "dashboard"
	| "plugSign"
	| "emissaoFiscal"
	| "ia"
	| "comunication"
	| "dfe";

type ConfiguracaoClienteSpace = {
	baseUrl: string;
	credentials: RequestCredentials;
	timeout: number | false;
	usarAutenticacao: boolean;
	injetarGrupoPadrao: boolean;
};

// Config compartilhada por todos os domínios. Sobrescreva por domínio no registro quando
// necessário (ex.: `report: { ...CONFIG_PADRAO, baseUrl, credentials: "omit" }`).
const CONFIG_PADRAO: Omit<ConfiguracaoClienteSpace, "baseUrl"> = {
	credentials: "include",
	timeout: false,
	usarAutenticacao: true,
	injetarGrupoPadrao: true,
};

const REGISTRO_CLIENTES_SPACE: Record<DominioClienteSpace, ConfiguracaoClienteSpace> = {
	authenticator: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_AUTHENTICATOR },
	estoqueService: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_ESTOQUE_SERVICE },
	pessoasService: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_PESSOAS_SERVICE },
	report: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_REPORT },
	compraInteligente: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_COMPRA_INTELIGENTE },
	utilitarios: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_UTILITARIOS_SERVICE },
	compraService: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_COMPRA_SERVICE },
	contasPagar: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_CONTAS_A_PAGAR_SERVICE },
	dashboard: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_DASHBOARD_SERVICE },
	plugSign: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_PLUG_SIGN },
	ia: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_IA },
	emissaoFiscal: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_EMISSAO_FISCAL_SERVICE },
	comunication: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_COMUNICATION },
	dfe: { ...CONFIG_PADRAO, baseUrl: env.VITE_API_BASE_URL_DFE },
};

const cacheClientes = new Map<DominioClienteSpace, KyInstance>();

// Copia os cabeçalhos globais em memória (Authorization, empresa-acesso, etc.),
// exceto o id-grupo, que tem resolução própria com precedência.
function copiarCabecalhosGlobais(requisicao: Request): Record<string, string> {
	const cabecalhosGlobais = getGlobalHeadersSpaceApi();
	Object.entries(cabecalhosGlobais).forEach(([chave, valor]) => {
		if (chave === DEFAULT_HEADER_GROUP_ID) return;
		requisicao.headers.set(chave, valor);
	});
	return cabecalhosGlobais;
}

// Precedência do id-grupo: header da request > header global > grupo padrão.
function resolverGrupoFinal(
	requisicao: Request,
	cabecalhosGlobais: Record<string, string>,
	injetarGrupoPadrao: boolean,
): string {
	const grupoRequest = normalizarCabecalhoGrupoSpaceApi(requisicao.headers.get(DEFAULT_HEADER_GROUP_ID));
	const grupoGlobal = normalizarCabecalhoGrupoSpaceApi(cabecalhosGlobais[DEFAULT_HEADER_GROUP_ID]);
	const grupoPadrao = injetarGrupoPadrao ? normalizarCabecalhoGrupoSpaceApi(DEFAULT_GROUP_ID) : "";
	return grupoRequest || grupoGlobal || grupoPadrao;
}

// Lê o corpo padrão de erro do backend uma única vez e o associa ao erro,
// para o queryClient/onError lerem message/severity de forma síncrona (obterErroApi).
async function anexarCorpoErroApiSpace(error: HTTPError): Promise<HTTPError> {
	const corpo = await lerCorpoErroApi(error.response);
	if (corpo) anexarErroApi(error, corpo);
	return error;
}

function criarClienteBase(configuracao: ConfiguracaoClienteSpace): KyInstance {
	const origem = new URL(configuracao.baseUrl);

	const clienteBase = ky.create({
		prefixUrl: origem.toString(),
		credentials: configuracao.credentials,
		timeout: configuracao.timeout,
		// Sem retry automático do ky; o retry de 401 (refresh) é feito por comAutenticacaoSpace.
		retry: 0,
		hooks: {
			beforeRequest: [
				(requisicao) => {
					const cabecalhosGlobais = copiarCabecalhosGlobais(requisicao);
					const grupoFinal = resolverGrupoFinal(
						requisicao,
						cabecalhosGlobais,
						configuracao.injetarGrupoPadrao,
					);

					if (grupoFinal) requisicao.headers.set(DEFAULT_HEADER_GROUP_ID, grupoFinal);
					else requisicao.headers.delete(DEFAULT_HEADER_GROUP_ID);
				},
			],
			beforeError: [anexarCorpoErroApiSpace],
		},
	});

	if (!configuracao.usarAutenticacao) return clienteBase;

	return comAutenticacaoSpace(clienteBase, [origem.origin]);
}

/** Base URL registrada para o domínio. Use quando a chamada não puder passar pelo cliente ky. */
export function obterBaseUrlSpace(dominio: DominioClienteSpace): string {
	return REGISTRO_CLIENTES_SPACE[dominio].baseUrl;
}

export function obterClienteSpace(dominio: DominioClienteSpace): KyInstance {
	const cacheExistente = cacheClientes.get(dominio);
	if (cacheExistente) return cacheExistente;

	const configuracao = REGISTRO_CLIENTES_SPACE[dominio];
	const cliente = criarClienteBase(configuracao);
	cacheClientes.set(dominio, cliente);

	return cliente;
}

/**
 * Para adicionar um novo domínio:
 * 1) inclua o domínio no tipo `DominioClienteSpace` e no `REGISTRO_CLIENTES_SPACE`
 *    (use `{ ...CONFIG_PADRAO, baseUrl: env.VITE_... }`, sobrescrevendo só o que diferir);
 * 2) exporte o cliente aqui (`export const spaceApiNovo = obterClienteSpace("novo")`).
 */
export const spaceApiEstoqueService: KyInstance = obterClienteSpace("estoqueService");
export const spaceApiReport: KyInstance = obterClienteSpace("report");
export const spaceApiCompraInteligente: KyInstance = obterClienteSpace("compraInteligente");
export const spaceApiUtilitarios: KyInstance = obterClienteSpace("utilitarios");
export const spaceApiCompraService: KyInstance = obterClienteSpace("compraService");
export const spaceApiContasAPagar: KyInstance = obterClienteSpace("contasPagar");
export const spaceApiAuthenticator: KyInstance = obterClienteSpace("authenticator");
export const spaceApiDashboard: KyInstance = obterClienteSpace("dashboard");
export const spaceApiPlugSign: KyInstance = obterClienteSpace("plugSign");
export const spaceApiEmissaoFiscal: KyInstance = obterClienteSpace("emissaoFiscal");
export const spaceApiPessoasService: KyInstance = obterClienteSpace("pessoasService");
export const spaceApiCommunication: KyInstance = obterClienteSpace("comunication");
export const spaceApiIA: KyInstance = obterClienteSpace("ia");
export const spaceApiDFE: KyInstance = obterClienteSpace("dfe");
